import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/hooks'
import { useAuth } from '../contexts/AuthContext'

export function usePropertyImport() {
  const { request } = useApi()
  const { orgCode } = useAuth()
  const queryClient = useQueryClient()

  const createPropertyMutation = useMutation({
    mutationFn: async (propertyData: any) => {
      const response = await request('/api/v1/organizations/{orgCode}/properties', 'POST', {
        params: { path: { orgCode: orgCode } },
        body: propertyData
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyDefinitions', orgCode] })
    }
  })

  const updatePropertyMutation = useMutation({
    mutationFn: async ({ uid, ...propertyData }: any) => {
      const response = await request('/api/v1/organizations/{orgCode}/properties/{propertyUid}', 'PATCH', {
        params: {
          path: {
            orgCode: orgCode,
            propertyUid: uid
          }
        },
        body: propertyData
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyDefinitions', orgCode] })
    }
  })

  const createEnumValueMutation = useMutation({
    mutationFn: async ({ propertyUid, enumValue }: { propertyUid: string; enumValue: any }) => {
      const response = await request('/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values', 'POST', {
        params: {
          path: {
            orgCode: orgCode,
            propertyUid: propertyUid
          }
        },
        body: {
          value: enumValue.value,
          name: enumValue.name || { en: enumValue.value },
          description: enumValue.description || {},
          order_index: enumValue.order_index || 0
        }
      })
      return response
    },
    onSuccess: () => {
      // Invalidate enum values queries to refresh the cache
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as any[]
          return queryKey.some(k =>
            typeof k === 'string' && k.includes('enum-values')
          )
        }
      })
    }
  })

  const importProperty = async (propertyData: any, action: 'create' | 'update') => {
    // Clean the data - remove metadata and extract enum values
    const { metadata: _metadata, enum_values, ...cleanData } = propertyData

    console.log('Importing property:', propertyData.code, 'Type:', propertyData.data_type)
    console.log('Has enum values:', !!enum_values, 'Count:', enum_values?.length)

    let propertyUid: string | undefined

    if (action === 'create') {
      const response = await createPropertyMutation.mutateAsync(cleanData)
      propertyUid = (response as any)?.uid
      console.log('Property created with UID:', propertyUid)
    } else if (action === 'update') {
      propertyUid = propertyData.uid
      await updatePropertyMutation.mutateAsync(cleanData)
      console.log('Property updated with UID:', propertyUid)
    }

    // If property is an enum and has enum values, import them
    if (
      propertyData.data_type === 'enum' &&
      enum_values &&
      Array.isArray(enum_values) &&
      enum_values.length > 0 &&
      propertyUid
    ) {
      console.log('Creating enum values for property:', propertyUid)

      // Sort enum values by order_index if available
      const sortedEnumValues = [...enum_values].sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
      console.log('Sorted enum values:', sortedEnumValues.map(v => v.value))

      // Create each enum value
      for (const enumValue of sortedEnumValues) {
        try {
          console.log('Creating enum value:', enumValue.value)
          await createEnumValueMutation.mutateAsync({
            propertyUid,
            enumValue
          })
          console.log('Successfully created enum value:', enumValue.value)
        } catch (error) {
          console.error('Failed to create enum value:', enumValue.value, error)
          // Continue with other enum values even if one fails
        }
      }
    } else {
      console.log('Skipping enum values creation:', {
        isEnum: propertyData.data_type === 'enum',
        hasEnumValues: !!enum_values,
        isArray: Array.isArray(enum_values),
        hasLength: enum_values?.length > 0,
        hasPropertyUid: !!propertyUid
      })
    }
  }

  return {
    importProperty,
    isLoading: createPropertyMutation.isPending || updatePropertyMutation.isPending || createEnumValueMutation.isPending
  }
}